import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Loader2, Target, TrendingUp, Wallet, CircleDollarSign, Clock, HandHeart } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { brl } from "@/lib/format";
import GoalFormModal from "@/components/GoalFormModal";
import AdminGoalsList from "@/components/AdminGoalsList";
import ContributionsPanel from "@/components/ContributionsPanel";
import NovaGoalPanel from "@/components/NovaGoalPanel";
import PrayerPanel from "@/components/PrayerPanel";
import ConfigPanel from "@/components/ConfigPanel";
import ReportPanel from "@/components/ReportPanel";
import SearchPanel from "@/components/SearchPanel";
import TestimonialsPanel from "@/components/TestimonialsPanel";

const TABS = [
  ["metas", "Metas / Boletos"],
  ["nova", "Nova Meta"],
  ["contribuicoes", "Contribuições"],
  ["oracoes", "Pedidos de Oração"],
  ["testemunhos", "Testemunhos"],
  ["relatorio", "Relatório"],
  ["busca", "Busca"],
  ["config", "Configurações"],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [metas, setMetas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("metas");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, s, c] = await Promise.all([api.get("/admin/metas"), api.get("/admin/stats"), api.get("/admin/config")]);
      setMetas(m.data);
      setStats(s.data);
      setCfg(c.data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === false) navigate("/adm", { replace: true });
    else if (user) load();
  }, [user, navigate, load]);

  if (user === null || (user && loading)) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-800" />
      </div>
    );
  }
  if (!user) return null;

  const pendingContributions = stats?.contribuicoes_pendentes || 0;

  const statCards = stats
    ? [
        { label: "Metas abertas", value: stats.metas_abertas, icon: Target, color: "text-blue-800 bg-blue-50", testid: "stat-metas-abertas" },
        { label: "Total das metas", value: brl(stats.total_metas), icon: Wallet, color: "text-slate-700 bg-slate-100", testid: "stat-total-metas" },
        { label: "Total arrecadado", value: brl(stats.total_arrecadado), icon: TrendingUp, color: "text-emerald-700 bg-emerald-50", testid: "stat-total-arrecadado" },
        { label: "Total faltante", value: brl(stats.total_faltante), icon: CircleDollarSign, color: "text-blue-800 bg-blue-50", testid: "stat-total-faltante" },
        { label: "Aguardando confirmação", value: stats.contribuicoes_pendentes, icon: Clock, color: "text-amber-700 bg-amber-50", testid: "stat-pendentes" },
        { label: "Pedidos de oração", value: stats.pedidos_oracao, icon: HandHeart, color: "text-rose-700 bg-rose-50", testid: "stat-oracoes" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="admin-dashboard-container">
      <header className="w-full bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.jpg" alt="Igreja Unida Deus Proverá" className="h-12 w-auto rounded-xl shrink-0 object-cover" />
            <div>
              <p className="font-heading text-lg font-black text-blue-900 leading-tight">Painel Administrativo</p>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-view-public-link"
              className="px-3 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-200 transition-colors"
            >
              Ver painel
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={async () => { await logout(); window.location.href = "/"; }}
              className="px-3 py-2 text-sm font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg border border-rose-200 transition-colors inline-flex items-center gap-1.5"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {statCards.map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <p className="mt-3 text-lg sm:text-xl font-extrabold text-slate-900 leading-tight">{s.value}</p>
              <p className="text-xs sm:text-sm text-slate-500 font-medium">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList
            className="bg-white border border-slate-200 rounded-xl p-1 h-auto w-full flex flex-wrap justify-start"
          >
            {TABS.map(([v, label]) => (
              <TabsTrigger
                key={v}
                data-testid={`tab-${v === "nova" ? "nova-meta" : v}`}
                value={v}
                className={[
                  "rounded-lg px-4 py-2.5 text-sm sm:text-base font-semibold whitespace-nowrap",
                  "data-[state=active]:bg-blue-800 data-[state=active]:text-white",
                ].join(" ")}
              >
                <span>{label}</span>
                {v === "contribuicoes" && pendingContributions > 0 && (
                  <span
                    data-testid="pending-contributions-badge"
                    aria-label={`${pendingContributions} contribuição${
                      pendingContributions === 1 ? "" : "ões"
                    } pendente${pendingContributions === 1 ? "" : "s"}`}
                    className={[
                      "ml-2 inline-flex min-w-5 h-5 items-center justify-center rounded-full",
                      "bg-rose-600 px-1.5 text-xs font-extrabold text-white",
                    ].join(" ")}
                  >
                    {pendingContributions > 99 ? "99+" : pendingContributions}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="metas" className="mt-6">
            <AdminGoalsList
              metas={metas}
              onEdit={(m) => { setEditing(m); setFormOpen(true); }}
              onChanged={load}
              ordenarPorVencimento={!!cfg?.ordenar_por_vencimento}
            />
          </TabsContent>
          <TabsContent value="nova" className="mt-6">
            <NovaGoalPanel onSaved={() => { load(); setTab("metas"); }} />
          </TabsContent>
          <TabsContent value="contribuicoes" className="mt-6">
            <ContributionsPanel onChanged={load} />
          </TabsContent>
          <TabsContent value="oracoes" className="mt-6">
            <PrayerPanel />
          </TabsContent>
          <TabsContent value="testemunhos" className="mt-6">
            <TestimonialsPanel key={tab} />
          </TabsContent>
          <TabsContent value="relatorio" className="mt-6">
            <ReportPanel key={tab} />
          </TabsContent>
          <TabsContent value="busca" className="mt-6">
            <SearchPanel />
          </TabsContent>
          <TabsContent value="config" className="mt-6">
            <ConfigPanel />
          </TabsContent>
        </Tabs>
      </main>

      {formOpen && (
        <GoalFormModal
          meta={editing}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}
    </div>
  );
}
